import React, { useContext, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import { vehicleContext } from '../VehicleProvider';
import HeaderUsedWrapper from './HeaderUsed';
import HeaderNewWrapper from './HeaderNew';
import { IS_T3_WEBSITE, IS_TESTING_ENVIRONMENT } from '../../constants/environment';

import HeaderTier3Wrapper from './HeaderTier3';
import useGetDealerDetails from '../../hooks/useGetDealerDetails';
import { createMetaTitle } from '../../utils/createMetaTitle';
import { createMetaDescription } from '../../utils/createMetaDescription';
import type { Impel360ViewCTA } from '../../types';
import { createRobotsTagNoIndex } from '../../utils/createRobotsTagNoIndex';
import mockViewContent from '../../mock-content.json';

interface VehicleHeaderProps {
  cta360View: Impel360ViewCTA;
}
const VehicleHeader: React.FC<VehicleHeaderProps> = ({ cta360View }) => {
  const vehicleViewCta = IS_TESTING_ENVIRONMENT ? mockViewContent : cta360View;

  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1299 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { vehicleData, loading } = useContext(vehicleContext);

  const vehicleType = vehicleData?.vehicleType;
  const isNew = vehicleType === 'new';

  const hasDealerImages = !!vehicleData?.vehicleDealerImages;

  const dealer = vehicleData?.kvpsSyncId;
  const { dealerInfo } = useGetDealerDetails(`USAA${dealer}`);

  const locationSearch = typeof window !== 'undefined' ? window.location.search : '';
  const vehicleId = new URLSearchParams(locationSearch).has('vehicleId');
  const market = new URLSearchParams(locationSearch).has('market');

  const robotNoIndexTag = useMemo(() => {
    return createRobotsTagNoIndex(market, vehicleId);
  }, [market, vehicleId]);

  const metaTitle = useMemo(() => {
    return createMetaTitle(vehicleData, dealerInfo);
  }, [dealerInfo, vehicleData]);

  const metaDescription = useMemo(() => {
    return createMetaDescription(vehicleData, dealerInfo);
  }, [dealerInfo, vehicleData]);

  // Temp over ride of meta description coming from Page: Flexible cfm
  // TODO: replace with page info service once it has been modified
  useEffect(() => {
    if (!document.querySelector('meta[name="description"]')) return;
    document.querySelector('meta[name="description"]')?.setAttribute('content', metaDescription);
  }, [metaDescription]);

  const renderContent = useMemo(() => {
    if (!loading && !vehicleData) return null;
    // Tier 3 New Vehicles
    if (IS_T3_WEBSITE && isNew && hasDealerImages) {
      return (
        <HeaderTier3Wrapper
          vehicleData={vehicleData}
          cta360View={vehicleViewCta}
          isTablet={isTablet}
          isMobile={isMobile}
          loading={loading}
        />
      );
    }
    // Tier 3 and Tier 1 Used Vehicles
    if (!isNew) {
      return (
        <HeaderUsedWrapper
          vehicleData={vehicleData}
          cta360View={vehicleViewCta}
          isTablet={isTablet}
          isMobile={isMobile}
          loading={loading}
        />
      );
    }
    // Tier 1 New Vehicles
    return (
      <HeaderNewWrapper
        vehicleData={vehicleData}
        cta360View={vehicleViewCta}
        isTablet={isTablet}
        isMobile={isMobile}
      />
    );
  }, [loading, vehicleData, isNew, hasDealerImages, vehicleViewCta, isTablet, isMobile]);

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{metaTitle}</title>
        {robotNoIndexTag}
      </Helmet>
      {renderContent}
    </>
  );
};

export default VehicleHeader;
